import { useState } from 'react';

import loadable from '@loadable/component';
import BpkBreakpoint, {
  BREAKPOINTS,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import LongArrowDownIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/long-arrow-down';
import LongArrowIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/long-arrow-right';
import LongArrowUpIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/long-arrow-up';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import CardRailWithThemeCard from '../CardRailWithThemeCard';
import CardStackWithThemeCard from '../CardStackWithThemeCard';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  CardDealsProps,
  ViewAllProps,
  ExpandableProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/CardDealsProps';

import STYLES from './CardDeals.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);
const LongArrowRight = withRtlSupport(LongArrowIcon);
const AlignedDownArrow = withRtlSupport(LongArrowDownIcon);
const AlignedUpArrow = withRtlSupport(LongArrowUpIcon);
const DESKTOP_DEFAULT_LIMIT = 6;
const DESKTOP_DEFAULT_LIMIT_V2 = 9;
const MOBILE_DEFAULT_LIMIT = 3;

const ViewAll = ({ redirectUrl, text }: ViewAllProps) => (
  <div className={STYLES.ViewAll}>
    <BpkButton
      className={STYLES.ViewAll__button}
      href={redirectUrl}
      rel="nofollow"
      type={BUTTON_TYPES.link}
      implicit
    >
      <BpkText textStyle={TEXT_STYLES.label1}>{text}</BpkText>
      <LongArrowRight />
    </BpkButton>
  </div>
);
type ExpandableControlProps = ExpandableProps & {
  isExpand: boolean;
  setExpand: (isExpand: boolean) => void;
};
const ExpandableControl = ({
  collapseText,
  expandText,
  isExpand,
  setExpand,
}: ExpandableControlProps) => {
  const onShowMoreClick = () => {
    setExpand(!isExpand);
  };
  return (
    <div className={STYLES.ViewAll}>
      <BpkButton
        className={STYLES.ViewAll__button}
        onClick={onShowMoreClick}
        type={BUTTON_TYPES.link}
        implicit
      >
        <BpkText textStyle={TEXT_STYLES.label1}>
          {isExpand ? collapseText : expandText}
        </BpkText>
        {isExpand ? <AlignedUpArrow /> : <AlignedDownArrow />}
      </BpkButton>
    </div>
  );
};

const CardDeals = ({
  cards,
  cardsV2,
  desktopCardLimit,
  disclaimer,
  expandable,
  grouping = false,
  imageConfig,
  isMobile,
  scroll = { desktop: false, mobile: false },
  subtitle,
  title,
  useThemeCardV2 = false,
  viewAll,
}: CardDealsProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  const [expand, setExpand] = useState(false);

  const activeCards = useThemeCardV2 ? cardsV2 : cards;
  if (!activeCards?.length) {
    return null;
  }

  const expandableControl = expandable ? (
    <ExpandableControl
      {...expandable}
      isExpand={expand}
      setExpand={setExpand}
    />
  ) : null;
  const viewAllAction = viewAll ? <ViewAll {...viewAll} /> : null;
  const desktopLimit = desktopCardLimit ?? DESKTOP_DEFAULT_LIMIT;
  let collapsedCardsV2Limit = desktopLimit;
  if (isMobile) {
    collapsedCardsV2Limit = MOBILE_DEFAULT_LIMIT;
  } else if (useThemeCardV2) {
    collapsedCardsV2Limit = DESKTOP_DEFAULT_LIMIT_V2;
  }

  const CardRail = (
    <CardRailWithThemeCard
      disclaimer={disclaimer}
      imageConfig={imageConfig}
      subtitle={subtitle}
      title={title}
      cards={cards}
      cardsV2={cardsV2}
      callToAction={viewAllAction}
      grouping={grouping}
      useThemeCardV2={useThemeCardV2}
    />
  );
  const CardStack = (
    <CardStackWithThemeCard
      disclaimer={disclaimer}
      imageConfig={imageConfig}
      subtitle={subtitle}
      title={title}
      cards={
        expandable || grouping
          ? cards
          : cards.slice(0, isMobile ? MOBILE_DEFAULT_LIMIT : desktopLimit)
      }
      cardsV2={
        expandable || grouping
          ? cardsV2
          : cardsV2?.slice(0, collapsedCardsV2Limit)
      }
      callToAction={expandableControl || viewAllAction}
      grouping={grouping}
      useThemeCardV2={useThemeCardV2}
      expandProps={
        expandable
          ? {
              isExpand: expand,
              maxToShowWhenCollapse:
                expandable?.maxToShowWhenCollapse ||
                (useThemeCardV2 ? (cardsV2?.length ?? 0) : cards.length),
            }
          : undefined
      }
    />
  );

  return (
    <BpkBreakpoint query={BREAKPOINTS.MOBILE} matchSSR={isMobile === true}>
      {(mobile: boolean) => {
        const deviceType = mobile ? 'mobile' : 'desktop';
        const displayMode =
          scroll.mobile || scroll.desktop ? 'scrollable' : 'stacked';
        const mbdProps = {
          device: deviceType,
          display_mode: displayMode,
        };

        if (
          (mobile && scroll.mobile === true) ||
          (!mobile && scroll.desktop === true)
        ) {
          return (
            <div
              data-testid="card-deals-with-scroll"
              data-tracking-element-id={rawComponentName}
              data-tracking-common-params={buildMBDParam(rawComponentName, {
                ...mbdProps,
                render_component_name: 'CardRailWithThemeCard',
              })}
              className={STYLES.CardDeals}
            >
              {CardRail}
            </div>
          );
        }
        return (
          <div
            data-testid="card-deals-with-stack"
            data-tracking-element-id={rawComponentName}
            data-tracking-common-params={buildMBDParam(rawComponentName, {
              ...mbdProps,
              render_component_name: 'CardStackWithThemeCard',
            })}
            className={STYLES.CardDeals}
          >
            {CardStack}
          </div>
        );
      }}
    </BpkBreakpoint>
  );
};

export default (props: CardDealsProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.CAR_HIRE_INDICATIVE_DEALS,
      })
    }
  >
    <CardDeals {...props} />
  </IntersectionObserverWrapper>
);
